import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  DOCUMENT,
  EmbeddedViewRef,
  Inject,
  Injectable,
  Injector,
  Type,
} from '@angular/core';
import { POPUP_DIALOG } from '@rd/core/tokens/popup.token';
import { take } from 'rxjs/operators';
import { DialogComponent } from '../../components/dialog/dialog.component';
import { DialogConfig } from '../../dialog-config';
import { DialogInjector } from '../../dialog-injector';
import { DialogRef, DialogRefCloseConfig } from '../../dialog-ref';
import { DialogContainerReadyService } from './dialog-container-ready.service';

@Injectable()
export class DialogService {
  dialogComponentRef: ComponentRef<DialogComponent>;
  currentDialogId = '';
  currentDialogRef: DialogRef | null = null;
  lastActiveElement: HTMLElement | null = null;

  constructor(
    @Inject(POPUP_DIALOG) private popupDialogComponentType: Type<any>,
    @Inject(DOCUMENT) private document: any,
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector,
    private containerReady: DialogContainerReadyService,
  ) {
    this.document = document as Document;
  }

  open<D>(componentType: Type<any>, config: DialogConfig<D>): DialogRef {
    const dialogRef = new DialogRef();

    this.containerReady.ready$.pipe(take(1)).subscribe(() => {
      this.appendComponentToContainer(config, dialogRef);
      this.dialogComponentRef.instance.childComponentType = componentType;
    });

    return dialogRef;
  }

  closeCurrentDialog(): void {
    if (this.checkDialogExistence() && this.currentDialogRef) {
      const sub = this.currentDialogRef.afterClosed.subscribe(() => {
        this.removeCurrentDialogState();
        this.removeComponentFromContainer();
        this.focusToLastActiveEl();
        sub.unsubscribe();
      });
      this.currentDialogRef.close();
    }
  }

  private checkDialogExistence(): boolean {
    return this.currentDialogId !== '' && !!this.currentDialogRef;
  }

  private appendComponentToContainer(
    config: DialogConfig,
    dialogRef: DialogRef,
  ): void {
    this.closeCurrentDialog();

    const map = new WeakMap();

    map.set(DialogConfig, config);
    map.set(DialogRef, dialogRef);

    this.currentDialogId = config.id;
    this.currentDialogRef = dialogRef;

    const sub = dialogRef.afterClosed.subscribe(
      (dialogRefCloseConfig: DialogRefCloseConfig) => {
        if (
          dialogRefCloseConfig?.isOverlayClick &&
          config.keepModalOnOverlayClick
        ) {
          return;
        }
        this.removeCurrentDialogState();
        this.removeComponentFromContainer();
        this.focusToLastActiveEl();
        sub.unsubscribe();
      },
    );

    const componentFactory =
      this.componentFactoryResolver.resolveComponentFactory(
        this.popupDialogComponentType,
      );
    const componentRef = componentFactory.create(
      new DialogInjector(this.injector, map),
    );

    this.appRef.attachView(componentRef.hostView);

    const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;
    this.document.querySelector('.rd-dialog-container')?.appendChild(domElem);
    this.document
      .querySelector('.main-container')
      ?.setAttribute('aria-hidden', true);
    this.document
      .querySelector('.main-container')
      ?.setAttribute('role', 'none');

    this.dialogComponentRef = componentRef;

    this.dialogComponentRef.instance.onClose.subscribe(() => {
      this.removeComponentFromContainer();
    });
  }

  private removeComponentFromContainer(): void {
    if (this.dialogComponentRef) {
      this.appRef.detachView(this.dialogComponentRef.hostView);
      this.dialogComponentRef.destroy();
      this.document
        .querySelector('.main-container')
        ?.removeAttribute('aria-hidden');
      this.document.querySelector('.main-container')?.removeAttribute('role');
    }
  }

  private removeCurrentDialogState(): void {
    this.currentDialogId = '';
    this.currentDialogRef = null;
  }

  private focusToLastActiveEl(): void {
    if (this.lastActiveElement) {
      this.lastActiveElement.focus();
      this.lastActiveElement = null;
    }
  }
}
